import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ErrorService } from '../../../shared/services/error.service';
import {
  ServiceOrderWorkSection,
  ServiceOrderWorkSectionsService
} from '../../../services/service-order-work-sections.service';
import { ExternalLinksService } from '../../../services/external-links.service';
import { BudgetServiceOrdersService } from '../../../services/budget-service-orders.service';

@Component({
  selector: 'app-aproval-service',
  templateUrl: './aproval-service.component.html',
  styleUrls: ['./aproval-service.component.css']
})
export class AprovalServiceComponent implements OnInit, OnDestroy {
  loading = true;
  approving = false;

  code: string | null = null;
  sectionId: number | null = null;
  externalLinkId: number | null = null;

  section: ServiceOrderWorkSection | null = null;

  private sub = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private externalLinksService: ExternalLinksService,
    private workSectionsService: ServiceOrderWorkSectionsService,
    private budgetService: BudgetServiceOrdersService,
    private errorService: ErrorService
  ) {}

  ngOnInit(): void {
    this.sub.add(
      this.route.queryParamMap.subscribe((params) => {
        this.code = params.get('code');

        const sectionIdRaw = params.get('sectionId');
        this.sectionId = sectionIdRaw ? Number(sectionIdRaw) : null;

        const externalLinkIdRaw = params.get('externalLinkId');
        this.externalLinkId = externalLinkIdRaw ? Number(externalLinkIdRaw) : null;

        if (!this.code || !this.sectionId || !Number.isFinite(this.sectionId)) {
          this.loading = false;
          this.errorService.showErrorMessage('Link inválido: parâmetros ausentes.');
          return;
        }

        this.loading = true;
        this.workSectionsService.getById(this.sectionId).pipe(
          catchError((err) => {
            this.errorService.showErrorMessage('Erro ao carregar a seção de serviço.');
            return of(null);
          })
        ).subscribe((section) => {
          this.section = section;
          this.loading = false;
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get servicesTotal(): number {
    return (this.section?.services || []).reduce((total, s) => total + (s.totalValue || 0), 0);
  }

  get itemsTotal(): number {
    return (this.section?.items || []).reduce((total, i) => total + (i.totalValue || 0), 0);
  }

  get grandTotal(): number {
    return this.servicesTotal + this.itemsTotal;
  }

  formatMoney(value: number): string {
    const safe = Number.isFinite(value) ? value : 0;
    return safe.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  aprovar(): void {
    if (!this.code || !this.sectionId) {
      this.errorService.showErrorMessage('Não foi possível aprovar: parâmetros ausentes.');
      return;
    }

    if (this.approving) return;

    if (!this.section) {
      this.errorService.showErrorMessage('Aguarde o carregamento da seção para aprovar.');
      return;
    }

    const serviceOrderId = this.section.serviceOrderId;
    if (!serviceOrderId || !Number.isFinite(serviceOrderId)) {
      this.errorService.showErrorMessage('Não foi possível aprovar: ServiceOrderId inválido na seção.');
      return;
    }

    this.approving = true;

    this.externalLinksService.validateCode(this.code).pipe(
      switchMap(() => this.workSectionsService.update(this.sectionId!, {
        serviceOrderId,
        description: this.section?.description,
        externalServiceProviderId: this.section?.externalServiceProviderId ?? null,
        // Status canônico alinhado à tela /b2b/budget-service-orders
        status: 'Orçamento-Aprovado'
      })),
      switchMap((updatedSection) => {
        // Sincroniza BugetServiceOrderTerciary (antes o link só atualizava a WorkSection).
        return this.budgetService.getPaginated(1, 50, { workSectionId: this.sectionId! }).pipe(
          catchError(() => of({ items: [] as any[] })),
          switchMap((page) => {
            const budgets = page?.items || [];
            if (!budgets.length) {
              return of(updatedSection);
            }
            const updates = budgets.map((b) =>
              this.budgetService.update(b.id!, {
                serviceOrderWorkSectionId: b.serviceOrderWorkSectionId,
                osNumber: b.osNumber,
                plateVechile: b.plateVechile,
                status: 'Aprovado',
                clientApproved: true
              }).pipe(catchError(() => of(null)))
            );
            return forkJoin(updates).pipe(map(() => updatedSection));
          })
        );
      }),
      switchMap((updatedSection) => {
        if (!this.externalLinkId || !Number.isFinite(this.externalLinkId)) {
          return of(updatedSection);
        }
        return this.externalLinksService.delete(this.externalLinkId).pipe(map(() => updatedSection));
      }),
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao aprovar a seção de serviço.');
        return of(null);
      })
    ).subscribe((updatedSection) => {
      this.approving = false;

      if (!updatedSection) {
        return;
      }

      this.section = updatedSection as ServiceOrderWorkSection;
      this.errorService.showSuccessMessage('Seção aprovada com sucesso.');
    });
  }
}
